"use client";

import React from "react";
import { FeatureCollection } from "geojson";
import { type CircleLayerSpecification, MapLayerMouseEvent } from "maplibre-gl";
import { RSource, RLayer } from "maplibre-react-components";
import { SiteType } from "@/types";
import { useMission, useSite, useMapLibre } from "@/hooks";
import { useTailwind } from "@oreme/ui";
import { filterGeoSitesByMission } from "@/utils";

interface SiteMapLibreLayerProps {
  data: FeatureCollection;
}

function SiteMapLibreGeoJSONLayer({ data }: Readonly<SiteMapLibreLayerProps>) {
  const { map } = useMapLibre();
  const { mission } = useMission();
  const tailwind = useTailwind();
  const { site, setSite, pinSite, unpinSite } = useSite();
  const memoizedData = React.useMemo(() => data, [data]);
  const [hover, setHover] = React.useState<number | string | null>(null);

  let filteredData = memoizedData;
  if (mission) filteredData = filterGeoSitesByMission(filteredData, mission);

  const paint: CircleLayerSpecification["paint"] = React.useMemo(
    () => ({
      "circle-color": [
        "case",
        ["boolean", ["feature-state", "hover"], false],
        tailwind.theme.colors.orange[500], // hover
        ["boolean", ["feature-state", "active"], false],
        tailwind.theme.colors.orange[500], // hover
        tailwind.theme.colors.blue[500], // default
      ],
      "circle-stroke-width": ["interpolate", ["linear"], ["zoom"], 1, 2, 12, 2],
      "circle-radius": ["interpolate", ["linear"], ["zoom"], 1, 2, 12, 5],
      "circle-stroke-color": [
        "case",
        ["boolean", ["feature-state", "hover"], false],
        tailwind.theme.colors.orange[700],
        ["boolean", ["feature-state", "active"], false],
        tailwind.theme.colors.orange[700], // hover
        tailwind.theme.colors.blue[700], // default
      ],
    }),
    [tailwind],
  );

  const handleMouseEnter = (e: MapLayerMouseEvent) => {
    if (!map) {
      return;
    }

    map.getCanvas().style.cursor = "pointer";

    const id = e.features ? e.features[0]["id"] : null;
    if (id) {
      setHover(id);
      pinSite(id);
    }
  };

  const handleMouseLeave = () => {
    if (!map) {
      return;
    }

    map.getCanvas().style.cursor = "";

    if (hover && hover !== site?.site_id) {
      unpinSite(hover);
    }
  };

  const handleClick = (e: MapLayerMouseEvent) => {
    const newSite = e.features ? (e.features[0].properties as SiteType) : null;
    if (newSite && newSite.site_id != site?.site_id) setSite(newSite);
  };

  return (
    <>
      <RSource
        key="source"
        id="source"
        generateId={true}
        type="geojson"
        data={filteredData}
        promoteId="site_id"
        cluster={false}
        clusterMaxZoom={14}
        clusterRadius={50}
      />
      <RLayer
        key="layer"
        id="layer"
        source="source"
        type="circle"
        paint={paint}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        onClick={handleClick}
      />
    </>
  );
}

export { SiteMapLibreGeoJSONLayer };
